/**
 * Defensive ISO timestamp formatter for backend-emitted dates.
 *
 * Backends emit ISO strings via Python `datetime.isoformat()` which
 * produce microseconds (6 fractional digits) and may or may not
 * carry a timezone marker. Some browsers don't normalize the
 * microsecond precision and a missing TZ marker would be parsed as
 * local-time instead of UTC.
 *
 * This helper:
 *   1. Trims microseconds to milliseconds.
 *   2. Appends `Z` (UTC) when no TZ marker is present.
 *   3. Returns the user-locale formatted string with the browser's
 *      timezone abbreviation appended (e.g. "EDT").
 *
 * Use this for ANY ISO timestamp coming from the FastAPI backend.
 *
 * @param {string} isoStr - ISO 8601 timestamp from the backend.
 * @returns {string} Locale-formatted string in the user's timezone.
 */
export function formatLocalDateTime(isoStr) {
  if (!isoStr) return 'N/A';
  let s = String(isoStr);
  // Python isoformat outputs 6-digit fractional seconds; ISO standard
  // is 3 digits. Trim defensively.
  s = s.replace(/(\.\d{3})\d+/, '$1');
  // No TZ marker → treat as UTC.
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    s = s + 'Z';
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return isoStr;
  try {
    return d.toLocaleString(undefined, { timeZoneName: 'short' });
  } catch (e) {
    return d.toLocaleString();
  }
}

/**
 * Format a date for a `<input type="datetime-local">` value
 * (YYYY-MM-DDTHH:MM, browser-local time).  NOT for display — use
 * `formatLocalDateTime` for human-readable output.  Returns '' for
 * missing/unparseable input so the field renders empty instead of
 * silently holding an invalid string the backend would reject.
 */
export function formatDateTimeLocalInput(date) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Same as `formatLocalDateTime` but date only (no time component).
 */
export function formatLocalDate(isoStr) {
  if (!isoStr) return 'N/A';
  // Plain YYYY-MM-DD: render without UTC shifting (date-only fields
  // should never time-zone shift).
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoStr)) {
    const [y, m, d] = isoStr.split('-').map((p) => parseInt(p, 10));
    return new Date(y, m - 1, d).toLocaleDateString();
  }
  let s = String(isoStr).replace(/(\.\d{3})\d+/, '$1');
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + 'Z';
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return isoStr;
  return dt.toLocaleDateString();
}

/**
 * Numeric M/D/YYYY date formatter (e.g. 10/10/2011).
 * Parses YYYY-MM-DD prefixes without timezone shift; falls back to
 * en-US short date for other parseable inputs, else returns the input.
 */
export function formatDateMDY(value) {
  if (!value) return '';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}`;
  const d = new Date(value);
  return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-US');
}
